import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder } from '@angular/forms';

import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Pros } from 'src/providers/pros/pros';
import { Communication } from 'src/providers/communication/communication';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';

import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';

@Component({
  selector: 'notify-pros-onboarding',
  templateUrl: './notify-pros-onboarding.component.html',
  styleUrls: ['./notify-pros-onboarding.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NotifyProsOnboardingComponent implements OnInit {
  private client = inject(Client);
  private rightSidePanelService = inject(RightSidePanelService);
  private navCtrl = inject(CustomNavController);
  private cdr = inject(ChangeDetectorRef);
  private util = inject(Util);
  private storage = inject(TidyStorage);
  private fb = inject(FormBuilder);
  private me = inject(Me);
  private pros = inject(Pros);
  private communication = inject(Communication);
  private onboardingProvider = inject(OnboardingProvider);
  windowService = inject(WindowService);

  private metadataKey: string;
  private readonly stepId = 'notify';
  private addressId: number;
  // Set when opened from the Get Started with TIDY onboarding flow; hides the Back / Skip
  // step controls (the step is reached from the action-items list, not a linear wizard).
  fromGettingStarted = false;

  allProItems: any[] = [];
  addressProItems: any[] = [];
  selectedPros: any[] = [];
  addressName = '';
  didCopyMessage = false;
  loaded = false;
  generatedMessage = '';
  messageCopied = false;
  userName = '[YOUR NAME]';
  businessName = '[YOUR BUSINESS]';
  sendingInProgress = false;
  prosSendStatus: { pro: any; status: 'pending' | 'sending' | 'success' | 'error'; error?: string }[] = [];
  howToSendItems = [
    { value: 'sms', viewValue: 'Automatically - send SMS to my pros for me' },
    { value: 'copy', viewValue: "Manually - I'll copy and paste the message" }
  ];
  proScopeItems: any[] = [];
  form = this.fb.group({
    useTodos: [true],
    useInvoicing: [false],
    useTimeTracking: [false],
    sendingRequests: [true],
    messageText: [''],
    howToSend: ['sms'],
    proScope: ['address']
  });

  async ngOnInit() {
    const params = await this.storage.retrieve('dialog-params');
    this.addressId = params?.addressId;
    this.fromGettingStarted = !!params?.fromGettingStarted;
    this.metadataKey = `onboarding_step.${this.addressId}.${this.stepId}`;
    this.rightSidePanelService.setDialogPageTitle('Notify Pros');
    this.loaded = false;
    try {
      const [meData, allPros, addressPros, addresses] = await Promise.all([
        this.me.load(),
        this.pros.getAllPrivatePros(),
        this.pros.getAllPrivateProsByAddress(this.addressId),
        this.client.getAddresses()
      ]);
      this.userName = meData.current_user?.name || '[YOUR NAME]';
      this.businessName = meData.customer_account?.account_name || meData.customer_account?.name || '[YOUR BUSINESS]';
      const address = addresses.find(a => a.id === this.addressId);
      this.addressName = address?.address_name || address?.address1 || 'this address';
      this.allProItems = allPros.map(pro => ({
        value: pro.id,
        viewValue: `${pro.first_name} ${pro.last_name}`
      }));
      this.addressProItems = addressPros.map(pro => ({
        value: pro.id,
        viewValue: `${pro.first_name} ${pro.last_name}`
      }));
      this.selectedPros = [...this.allProItems];
      this.proScopeItems = [
        { value: 'address', viewValue: `All pros at ${this.addressName}` },
        { value: 'all', viewValue: 'All pros' },
        { value: 'select', viewValue: 'Select pros' }
      ];
      const hasTodos = await this.onboardingProvider.checkIfDidFinishToDosOnboarding();
      if (hasTodos) {
        this.form.patchValue({ useTodos: true });
      }
      this.generateMessage();
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  generateMessage = () => {
    const name = this.userName;
    const business = this.businessName;
    const message = `Hi, it's ${name} from ${business}.

📱 This is my new number - please use this for all communication.

I'm using TIDY to help me manage my property. They will chat with you on this line too.

Don't worry, it should mean very little changes for you:
✓ Pay stays the same
✓ No app required (they have one if you like)

Next steps:
📆 Keep doing any scheduled jobs.
👍 Accept new job requests that come in.

I appreciate you and am excited to keep working together.`;
    this.generatedMessage = message;
    this.form.patchValue({ messageText: message });
  };

  updateSelectedPros = (pro: any) => {
    if (this.selectedPros.includes(pro)) {
      this.selectedPros = this.selectedPros.filter(p => p !== pro);
    } else {
      this.selectedPros.push(pro);
    }
  };

  copyMessage = () => {
    this.didCopyMessage = true;
    const messageToSend = this.form.get('messageText')?.value || this.generatedMessage;
    const textArea = document.createElement('textarea');
    textArea.value = messageToSend;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    this.messageCopied = true;
    this.cdr.markForCheck();
    setTimeout(() => {
      this.messageCopied = false;
      this.cdr.markForCheck();
    }, 3000);
  };

  private getProsToSend(): any[] {
    const scope = this.form.value.proScope;
    if (scope === 'address') {return this.addressProItems;}
    if (scope === 'all') {return this.allProItems;}
    return this.selectedPros;
  }

  sendSMSToPros = async () => {
    try {
      const prosToSend = this.getProsToSend();
      if (prosToSend.length === 0) {
        return this.util.showError('No pros selected to send to.');
      }
      this.sendingInProgress = true;
      this.prosSendStatus = prosToSend.map(pro => ({
        pro,
        status: 'pending' as const
      }));
      this.cdr.markForCheck();
      for (let i = 0; i < this.prosSendStatus.length; i++) {
        const item = this.prosSendStatus[i];
        item.status = 'sending';
        this.cdr.markForCheck();
        try {
          const messageToSend = this.form.get('messageText')?.value || this.generatedMessage;
          const payload = {
            message: {
              text: messageToSend,
              files: []
            },
            to_homekeeper_id: item.pro.value
          };
          await this.communication.sendSharedInboxMessage(payload);
          item.status = 'success';
        } catch (error) {
          item.status = 'error';
          item.error = error?.error?.message || 'Failed to send message';
        }
        this.cdr.markForCheck();
        if (i < this.prosSendStatus.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      const successCount = this.prosSendStatus.filter(item => item.status === 'success').length;
      const errorCount = this.prosSendStatus.filter(item => item.status === 'error').length;
      if (successCount > 0 && errorCount === 0) {
        this.completeStep();
      } else if (successCount > 0 && errorCount > 0) {
        this.util.showWarning(`Message sent to ${successCount} pros. Failed for ${errorCount} pros.`);
      } else {
        this.util.showError('Failed to send messages to pros');
      }
    } catch (err) {
      this.util.showError((err.error && err.error.message) ? err.error.message : err.message, 10000);
    } finally {
      this.sendingInProgress = false;
      this.cdr.markForCheck();
    }
  };

  completeStep = async () => {
    if (!this.didCopyMessage && this.form.value.howToSend === 'copy') {
      return this.util.showError('Please copy the message first');
    }
    try {
      // Notify Pros is an independent flow: it writes ONLY `notify=completed`. Add Pros is a
      // separate step that owns the `pros` signal.
      await this.client.addMetadata({ key: this.metadataKey, value: 'completed' });
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };

  goBack = () => {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.back();
    }
  };

  skipStep = async () => {
    try {
      // Notify Pros writes ONLY `notify=completed` (see completeStep).
      await this.client.addMetadata({ key: this.metadataKey, value: 'completed' });
      if (this.windowService.isDesktopRes) {
        this.rightSidePanelService.closeRightSidePanel();
      } else {
        this.navCtrl.navigateForward('getting-started');
      }
    } catch (err) {
      this.util.showError('Failed to complete step. Please try again.');
    }
  };
}
